import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  AlertTriangle, Loader2, Send, Edit, Users, Calendar, Clock, MapPin, PoundSterling
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import OrderChip from '../Brand/OrderChip';
import { SkeletonCards } from '../Brand/BrandLoading';
import EmptyState from '../ui/empty-state';
import { ukDate, ukTime, money } from '../../lib/format';
import JobCocktailStatus from '../Cocktails/JobCocktailStatus';
import JobEmailPanel from '../Emails/JobEmailPanel';
import BookingTextButton from './BookingTextButton';
import JobAvailability from '../Availability/JobAvailability';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HoldingZone = ({ token, onEdit, onJobsChanged, refreshKey }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [quiet, setQuiet] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/jobs/holding`, { headers });
      setJobs(res.data.jobs || []);
    } catch (err) {
      toast.error('Could not load the holding list');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const toggle = (jobId) => {
    setSelected((prev) => prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]);
  };

  const release = async (jobIds) => {
    if (jobIds.length === 0) return;
    setReleasing(true);
    try {
      const res = await axios.post(`${API}/admin/jobs/release`, { job_ids: jobIds, quiet }, { headers });
      toast.success(
        `Released ${res.data.released_count} job${res.data.released_count > 1 ? 's' : ''}` +
        (res.data.quiet ? ' quietly (no alerts)' : ` · ${res.data.alerts_sent} staff alerted`)
      );
      setSelected([]);
      await load();
      onJobsChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not release those jobs');
    } finally {
      setReleasing(false);
    }
  };

  if (loading) {
    return <SkeletonCards count={3} testId="holding-loading" />;
  }

  return (
    <div className="space-y-4" data-testid="holding-zone">
      <div className="rounded-[10px] border border-hair bg-cream p-3 text-sm text-ink bd-card-rule">
        Jobs here are <strong>not visible to staff</strong>. Edit and assign them as normal — anyone you assign gets
        the job straight away. Press <strong>Release to Available</strong> when you want the rest of the team to see it.
      </div>

      {jobs.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Nothing in holding"
          line="New and imported jobs land here first, before staff can see them."
          testId="holding-empty"
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 rounded-[10px] border border-hair bg-white p-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Checkbox
                checked={selected.length === jobs.length && jobs.length > 0}
                onCheckedChange={(checked) => setSelected(checked ? jobs.map((j) => j.id) : [])}
                data-testid="holding-select-all"
              />
              Select all ({jobs.length})
            </label>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={quiet} onCheckedChange={(c) => setQuiet(!!c)} data-testid="holding-quiet-checkbox" />
              Release quietly (no alerts)
            </label>

            <Button
              className="ml-auto bg-green-600 hover:bg-green-700"
              disabled={selected.length === 0 || releasing}
              onClick={() => release(selected)}
              data-testid="release-selected-btn"
            >
              {releasing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Release {selected.length > 0 ? `${selected.length} ` : ''}to Available
            </Button>
          </div>

          <div className="grid gap-4">
            {jobs.map((job) => (
              <Card key={job.id} className="bg-white border-l-[3px] border-l-gold" data-testid={`holding-job-${job.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <Checkbox
                        checked={selected.includes(job.id)}
                        onCheckedChange={() => toggle(job.id)}
                        className="mt-1"
                        data-testid={`holding-select-${job.id}`}
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-ink">{job.job_type}</h3>
                          <OrderChip value={job.order_number || job.job_number} />
                          <Badge className="bg-amber-100 text-amber-800 text-xs">Holding</Badge>
                          {job.import_source === 'spreadsheet_import' && (
                            <Badge variant="outline" className="text-xs text-slate-500">imported</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{job.client_name}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-2">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{ukDate(job.date)}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{job.time ? (ukTime(job.time) || job.time) : 'No time'}</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{job.location || 'No address'}</span>
                          <span className="flex items-center gap-1"><PoundSterling className="w-3 h-3" />{job.wage ? `${money(job.wage)} wage` : 'No wage'}</span>
                        </div>
                        {job.assigned_staff_details?.length > 0 && (
                          <p className="text-xs text-gold-deep mt-2">
                            Assigned: {job.assigned_staff_details.map((s) => s.name).join(', ')} — they can see this job already
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 flex-shrink-0">
                      <BookingTextButton job={job} token={token} />
                      <Button size="sm" variant="outline" onClick={() => onEdit(job)} data-testid={`holding-edit-${job.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm" className="bg-green-600 hover:bg-green-700"
                        disabled={releasing}
                        onClick={() => release([job.id])}
                        data-testid={`holding-release-${job.id}`}
                      >
                        Release
                      </Button>
                    </div>
                  </div>

                  {job.warnings?.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2" data-testid={`holding-warnings-${job.id}`}>
                      <AlertTriangle className="w-4 h-4 text-amber-700" />
                      {job.warnings.map((warning, i) => (
                        <Badge key={i} variant="outline" className="text-xs border-amber-300 text-amber-800 bg-amber-50">
                          {warning}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <JobCocktailStatus job={job} token={token} onChanged={load} />
                  <JobAvailability job={job} token={token} />
                  <JobEmailPanel job={job} token={token} />
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default HoldingZone;
